import React, { JSXElementConstructor, ReactElement } from 'react';
import styled from 'styled-components';
import iconMixin from '../../styles/utils/iconMixin';
import { makeStyles, Tooltip } from '@fluentui/react-components';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { isReactMemoOrLazy } from '../react-data-grid/utils/isFunction';

export const HeaderStatusIcon = styled.div`
  ${iconMixin('15px')};
  padding-right: 5px;
  vertical-align: text-bottom;
`;

const Container = styled.div`
  border-radius: 3px;
  display: inline-flex;
  font-weight: 400;
  font-size: 11px;
  height: 18px;
  line-height: 14px;
  margin-right: 5px;
  padding: 2px 5px 2px 3px;
`;

const useStatusStyles = makeStyles({
  statusFlagBasic: { backgroundColor: p4Tokens.statusFlagBasic, color: p4Tokens.colorNeutralForegroundStaticInverted },
  statusFlagDanger: {
    backgroundColor: p4Tokens.statusFlagDanger,
    color: p4Tokens.colorNeutralForegroundStaticInverted,
  },
  statusFlagSuccess: {
    backgroundColor: p4Tokens.statusFlagSuccess,
    color: p4Tokens.colorNeutralForegroundStaticInverted,
  },
  statusFlagWarning: {
    backgroundColor: p4Tokens.statusFlagWarning,
    color: p4Tokens.colorNeutralForegroundStaticInverted,
  },
  statusFlagInfo: { backgroundColor: p4Tokens.statusFlagInfo, color: p4Tokens.colorNeutralForegroundStaticInverted },
  statusFlagReady: {
    backgroundColor: p4Tokens.statusFlagReady,
    color: p4Tokens.colorNeutralForegroundStaticInverted,
  },
});

export enum StatusStyle {
  Danger = 'danger', //Red
  Success = 'success', //Green
  Warning = 'warning', //Amber
  Info = 'info', //Teal
  Basic = 'basic', //Grey
  Ready = 'ready', //Blue
}

const useStatusClassname = (statusStyle?: StatusStyle) => {
  const styles = useStatusStyles();
  switch (statusStyle as StatusStyle & 'error') {
    case 'error':
    case 'danger':
      return styles.statusFlagDanger;
    case 'success':
      return styles.statusFlagSuccess;
    case 'warning':
      return styles.statusFlagWarning;
    case 'info':
      return styles.statusFlagInfo;
    case 'ready':
      return styles.statusFlagReady;
    case 'basic':
      return styles.statusFlagBasic;
    default:
      return styles.statusFlagBasic;
  }
};

const HeaderStatus = ({
  icon,
  statusStyle,
  title,
  tooltip,
}: {
  icon?: string | JSXElementConstructor<any>;
  title?: string;
  statusStyle?: StatusStyle;
  tooltip?: string;
}) => {
  let statusIcon: ReactElement | null = null;
  let usesFluentIcon = false;
  if (isReactMemoOrLazy(icon)) {
    //Note - Expecting a fluent-ui v9 icon component here (Non-fluent versions provide a string).
    const Icon = icon as JSXElementConstructor<any>;
    usesFluentIcon = true;
    statusIcon = <Icon />;
  } else if (icon) {
    statusIcon = <HeaderStatusIcon>{icon}</HeaderStatusIcon>;
  }

  const className = useStatusClassname(statusStyle);

  const content = (
    <Container className={className} style={usesFluentIcon ? { lineHeight: '16px', height: '19px' } : undefined}>
      {statusIcon}
      {title}
    </Container>
  );

  const tooltipContent = tooltip ?? title;

  return (
    <Tooltip content={tooltipContent} relationship="description" withArrow={true}>
      {content}
    </Tooltip>
  );
};

export default HeaderStatus;
